import { useEffect, useState } from "react";
import axios from "axios";
import {
    Line
} from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend
} from "chart.js";
import { FaArrowLeftLong, FaArrowRightLong } from "react-icons/fa6";
import { formatSalesValue } from "../../utils/formatUtils";
import { formatDisplayDate } from "../../utils/formateDateUtils";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend
);

const formatDate = (d) => d.toLocaleDateString('en-CA');

const addDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
};

const WeeklySalesChart = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = localStorage.getItem("token");
    const userID = localStorage.getItem("userID");

    const today = new Date();
    const todayStr = formatDate(today);

    // default range = today and 6 days before
    const [endDate, setEndDate] = useState(today);
    const [startDate, setStartDate] = useState(addDays(today, -6));

    const [chartData, setChartData] = useState(null);
    const [rawData, setRawData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Generate colors for outlets
    const generateColors = (outlets) => {
        const colors = [
            "#FF6B6B", "#4ECDC4", "#FFD166", "#06D6A0", "#118AB2",
            "#073B4C", "#EF476F", "#7209B7", "#3A86FF", "#FB5607",
            "#8338EC", "#FF006E", "#FF9E00", "#1B9AAA", "#E63946"
        ];

        const colorMap = {};
        outlets.forEach((outlet, idx) => {
            colorMap[outlet] = colors[idx % colors.length];
        });
        return colorMap;
    };

    // ---------- Fetch ----------
    const fetchWeeklySales = async () => {
        try {
            setLoading(true);

            const payload = {
                outlet_name: "ALL",
                start_date: formatDate(startDate),
                end_date: formatDate(endDate),
                token: token,
                employee_id: Number(userID)
            };

            const res = await axios.post(
                `${baseUrl}/getweeklysales`,
                payload
            );

            const data = res.data;
            setRawData(data);

            const labels = data.days.map(d => d.date);
            const outlets = data.outlets || [];

            // Generate colors for outlets
            const colors = generateColors(outlets);

            const datasets = outlets.map((outlet) => {
                return {
                    label: outlet,
                    data: data.days.map(day => day.outlets[outlet] || 0),
                    tension: 0.3,
                    borderColor: colors[outlet],
                    backgroundColor: colors[outlet],

                    pointBorderColor: data.days.map(day =>
                        day.date === todayStr ? "#E5E7EB" : "#ffffff"
                    ),
                    pointBorderWidth: data.days.map(day =>
                        day.date === todayStr ? 3 : 2
                    ),
                    pointRadius: data.days.map(day =>
                        day.date === todayStr ? 6 : 4
                    ),
                    pointHoverRadius: data.days.map(day =>
                        day.date === todayStr ? 8 : 6
                    ),
                };
            });

            setChartData({
                labels,
                datasets
            });

        } catch (err) {
            console.error(err);
            setError(err.response?.data?.error ||
                err.response?.data?.message ||
                err.message ||
                "Failed to fetch weekly sales data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWeeklySales();
    }, [startDate, endDate]);

    // ---------- Navigation ----------
    const goPrevWeek = () => {
        const newEnd = addDays(startDate, -1);
        const newStart = addDays(newEnd, -6);
        setStartDate(newStart);
        setEndDate(newEnd);
    };

    const goNextWeek = () => {
        const newStart = addDays(endDate, 1);
        const newEnd = addDays(newStart, 6);

        if (newEnd > today) return;

        setStartDate(newStart);
        setEndDate(newEnd);
    };

    const isCurrentWeek = formatDate(endDate) === formatDate(today);

    // ---------- Custom tooltip ----------
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "bottom",
            },
            tooltip: {
                mode: 'index',
                intersect: false,
                displayColors: true,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleFont: {
                    size: 14,
                    weight: 'bold'
                },
                bodyFont: {
                    size: 13
                },
                padding: 12,

                callbacks: {
                    title: function (context) {
                        return context[0].label;
                    },

                    // This shows text per outlet
                    label: function (context) {
                        return `${context.dataset.label}: ${formatSalesValue(context.raw)}`;
                    },

                    // This controls the color box
                    labelColor: function (context) {
                        return {
                            borderColor: context.dataset.borderColor,
                            backgroundColor: context.dataset.borderColor,
                            borderWidth: 2,
                            borderRadius: 2
                        };
                    },

                    footer: function () {
                        return '';
                    }
                }
            }
        },

        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Sales Amount'
                }
            },
            x: {
                title: {
                    display: true,
                    text: 'Date'
                },
                ticks: {
                    callback: function (value) {
                        const label = this.getLabelForValue(value);
                        return label === todayStr ? "Today" : formatDisplayDate(label);
                    },

                    color: function (context) {
                        const label = context.chart.data.labels[context.index];
                        return label === todayStr ? "#374151" : "#6B7280";
                    },

                    font: function (context) {
                        const label = context.chart.data.labels[context.index];

                        if (label === todayStr) {
                            return {
                                weight: "bold",
                                size: 14
                            };
                        }

                        return {
                            weight: "normal",
                            size: 12
                        };
                    }
                }
            }
        },
        // Make points always visible on hover
        interaction: {
            mode: 'index',
            intersect: false,
        },
        // Show points always
        elements: {
            point: {
                hoverRadius: 8,
                hoverBorderWidth: 3,
            }
        },
        // Enable hover for entire dataset
        hover: {
            mode: 'index',
            intersect: false
        }
    };

    const sortedDays = rawData?.days
        ? [...rawData.days].sort(
            (a, b) => new Date(b.date) - new Date(a.date)
        )
        : [];

    const isToday = (date) => {
        return formatDate(new Date(date)) === formatDate(today);
    };

    return (
        <div className="bg-white rounded-lg shadow-lg p-3 lg:p-4 w-full">
            <h2 className="text-lg lg:text-xl font-semibold text-center">
                Weekly Sales
            </h2>

            <div className="flex items-center justify-between mb-3 px-3">
                {/* Date Range */}
                <div className="text-xs lg:text-sm font-semibold whitespace-nowrap text-gray-500">
                    {formatDate(startDate)} → {formatDate(endDate)}
                </div>

                <div className="flex items-center gap-3 text-gray-500 hover:text-gray-600">
                    <button
                        onClick={goPrevWeek}
                        className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200"
                    >
                        <FaArrowLeftLong />
                    </button>

                    <button
                        disabled={isCurrentWeek}
                        onClick={goNextWeek}
                        className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed"
                    >
                        <FaArrowRightLong />
                    </button>
                </div>
            </div>

            {/* Chart */}
            {loading && (
                <div className="flex items-center justify-center h-full">
                    Loading...
                </div>
            )}

            {error && (
                <div className="text-center text-red-600 font-medium">
                    {error}
                </div>
            )}

            <div className="w-full h-[350px] mx-auto">
                {!loading && !error && chartData && (
                    <Line data={chartData} options={options} className="w-full h-full mx-auto" />
                )}
            </div>

            {/* Sales Table */}
            {!loading && !error && rawData && (
                <div className="mt-6 overflow-x-auto">
                    <table className="min-w-full border border-gray-200 text-xs">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="border p-2 text-left font-semibold whitespace-nowrap sticky left-0 bg-gray-100">
                                    Date
                                </th>

                                {rawData.outlets.map((outlet, index) => (
                                    <th
                                        key={index}
                                        className="border p-2 text-left font-semibold whitespace-nowrap"
                                    >
                                        {outlet}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {sortedDays.map((day, rowIndex) => (
                                <tr
                                    key={rowIndex}
                                    className={`hover:bg-gray-50 ${isToday(day.date) ? "bg-gray-200" : ""
                                        }`}
                                >
                                    <td className={`border p-2 font-semibold whitespace-nowrap sticky left-0 ${isToday(day.date) ? "bg-gray-200 text-sm" : "bg-white"
                                        }`}>
                                        {isToday(day.date)
                                            ? "Today"
                                            : formatDisplayDate(day.date)}
                                    </td>

                                    {rawData.outlets.map((outlet, colIndex) => (
                                        <td
                                            key={colIndex}
                                            className={`border p-2 ${isToday(day.date) ? "bg-gray-200 text-sm font-semibold" : "bg-white"}`}
                                        >
                                            {formatSalesValue(
                                                day.outlets[outlet] || 0
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}

                            <tr className="bg-gray-100 font-semibold whitespace-nowrap">
                                <td className="border p-2 sticky left-0 bg-gray-100">
                                    Total
                                </td>

                                {rawData.outlets.map((outlet, index) => (
                                    <td key={index} className="border p-2">
                                        {formatSalesValue(
                                            rawData.days.reduce((sum, day) => {
                                                return sum + (day.outlets[outlet] || 0);
                                            }, 0)
                                        )}
                                    </td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default WeeklySalesChart;